if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        console.debug('MediaGallery init', this.id, 'thumbnails:', !!this.elements.thumbnails);

        this.elements.viewer && this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        
        // Auto-slide functionality
        this.autoSlideInterval = null;
        this.isUserInteracting = false;
        this.autoSlideDelay = 2000; // 2 seconds
        this.currentSlideIndex = 0;

        // Cache thumbnail items if present and determine starting index
        this.thumbnailItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        if (this.thumbnailItems.length) {
          const activeIndex = this.thumbnailItems.findIndex((t) => t.querySelector('button')?.getAttribute('aria-current'));
          this.currentSlideIndex = activeIndex >= 0 ? activeIndex : 0;
        }

        // Start auto-slide after component is ready (only if thumbnails exist)
        setTimeout(() => {
          if (this.thumbnailItems.length > 1) this.startAutoSlide();
          else console.debug('MediaGallery: auto-slide not started - insufficient thumbnails', this.id);
        }, 1000);
        
        // Pause auto-slide on user interaction
        this.addEventListener('mouseenter', () => {
          this.pauseAutoSlide();
        });
        
        this.addEventListener('mouseleave', () => {
          this.startAutoSlide();
        });
        
        this.addEventListener('click', (e) => {
          if (e.target.closest('.thumbnail-list, .media-list, button')) {
            this.pauseAutoSlide();
            setTimeout(() => {
              if (!this.isUserInteracting) {
                this.startAutoSlide();
              }
            }, 5000); // Resume after 5 seconds
          }
        });

        // Also treat pointer/wheel/touch events as user interaction so programmatic
        // scrolling doesn't fight manual scroll. Resume auto-slide after a short delay.
        let _userInteractTimer = null;
        const pauseForUserInteraction = () => {
          this.pauseAutoSlide();
          this.isUserInteracting = true;
          clearTimeout(_userInteractTimer);
          _userInteractTimer = setTimeout(() => {
            this.isUserInteracting = false;
            this.startAutoSlide();
          }, 1000);
        };

        this.addEventListener('wheel', pauseForUserInteraction, { passive: true });
        this.addEventListener('touchstart', pauseForUserInteraction, { passive: true });
        this.addEventListener('pointerdown', pauseForUserInteraction, { passive: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          // Only reorder nodes if they already belong to this gallery's expected containers.
          // This prevents accidental reparenting across unrelated DOM regions (which
          // can cause thumbnails to appear inside editorial sections when layouts differ).
          try {
            if (this.elements.viewer && this.elements.viewer.contains(activeMedia)) {
              const mediaParent = activeMedia.parentElement;
              if (mediaParent && this.elements.viewer.contains(mediaParent)) {
                if (mediaParent.firstChild !== activeMedia) mediaParent.prepend(activeMedia);
              }
            }

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail && this.elements.thumbnails.contains(activeThumbnail)) {
                const thumbParent = activeThumbnail.parentElement;
                if (thumbParent && this.elements.thumbnails.contains(thumbParent)) {
                  if (thumbParent.firstChild !== activeThumbnail) thumbParent.prepend(activeThumbnail);
                }
              }
            }

            if (this.elements.viewer && this.elements.viewer.slider) this.elements.viewer.resetPages();
          } catch (e) {
            // Fail-safe: if anything goes wrong, avoid reparenting and log in dev
            console.warn('MediaGallery: safe-prepend skipped due to', e);
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Skip horizontal scroll for desktop thumbnail layouts
          const isDesktopThumbnail = this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');
          if (!isDesktopThumbnail && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Read sticky header height (set as CSS variable by sticky-header)
          const headerHeightVal = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0px';
          const headerHeight = parseInt(headerHeightVal, 10) || 0;
          // If the media is already visible below the header, do nothing.
          const visibleThreshold = 8; // small slack to avoid tiny adjustments
          if (activeMediaRect.top >= headerHeight + visibleThreshold) return;

          // Compute target scroll position adjusted for sticky header
          const targetTop = Math.max(0, window.scrollY + activeMediaRect.top - headerHeight - visibleThreshold);

          // Only programmatically scroll on medium (tablets) where it improves UX.
          // Avoid automatic vertical scrolling on large desktop to prevent jumping.
          // FIX: Removed window.scrollTo to prevent stealing user scroll position during auto-slide.
          const isTablet = window.matchMedia && window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      startAutoSlide() {
        if (this.autoSlideInterval) return;
        if (!this.thumbnailItems || this.thumbnailItems.length <= 1) return;
        console.debug('MediaGallery: startAutoSlide', this.id, 'delay', this.autoSlideDelay);

        this.autoSlideInterval = setInterval(() => {
          if (!this.isUserInteracting) {
            this.nextSlide();
          }
        }, this.autoSlideDelay);
      }
      
      pauseAutoSlide() {
        this.isUserInteracting = true;
        if (this.autoSlideInterval) {
          clearInterval(this.autoSlideInterval);
          this.autoSlideInterval = null;
          console.debug('MediaGallery: pauseAutoSlide', this.id);
        }
      }
      
      nextSlide() {
        if (!this.thumbnailItems || this.thumbnailItems.length <= 1) return;

        this.currentSlideIndex = (this.currentSlideIndex + 1) % this.thumbnailItems.length;
        const nextThumbnail = this.thumbnailItems[this.currentSlideIndex];
        if (!nextThumbnail) return;

        const target = nextThumbnail.dataset.target;
        console.debug('MediaGallery: nextSlide ->', target, 'index', this.currentSlideIndex);
        if (target) this.setActiveMedia(target, false);
      }
    }
  );
}
